
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Factura, Cliente, InvoiceStatus } from '@shared/types';
import { auth } from '@/config/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AnularFacturaDialog } from '@/components/facturas/AnularFacturaDialog';
import { ImportFacturaDialog } from '@/components/facturas/ImportFacturaDialog';
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableExport } from '@/components/DataTableExport';
import { EntornoAEATBadge } from '@/components/verifactu/EntornoAEATBadge';
import { Plus, Pencil, ChevronsUpDown, Loader2, FileText, FileCode, Shield, ShieldCheck, ShieldAlert, Download, CheckCircle2, Send, Ban, Building2, Upload, Check, ChevronDown } from 'lucide-react';

// Tipos
type EnrichedFactura = Factura & { clienteNombre: string };
type SortKey = keyof EnrichedFactura | 'total';

// Constantes y Helpers
const FACTURA_COLUMNS: Array<{ id: keyof EnrichedFactura; name: string }> = [
  { id: 'numero', name: 'Nº Factura' },
  { id: 'clienteNombre', name: 'Cliente' },
  { id: 'fecha', name: 'Fecha Emisión' },
  { id: 'fechaVencimiento', name: 'Fecha Vencimiento' },
  { id: 'total', name: 'Total' },
  { id: 'estado', name: 'Estado' },
];

const getStatusBadgeVariant = (status: InvoiceStatus) => {
  const variants: Partial<Record<InvoiceStatus, 'success' | 'destructive' | 'default' | 'secondary'>> = {
    'Pagada': 'success',
    'Vencida': 'destructive',
    'Emitida': 'default',
    'Borrador': 'secondary',
    'Anulada': 'destructive',
  };
  return variants[status] || 'default';
};

// Estados de cobro que el usuario puede asignar a una factura emitida
const ESTADOS_COBRO: InvoiceStatus[] = ['Emitida', 'Pagada', 'Vencida'];

export default function FacturasPage() {
  const { fetchWithAuth } = useUser();
  const navigate = useNavigate();
  const [facturas, setFacturas] = useState<Factura[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({ key: 'fecha', direction: 'desc' });
  const [anularId, setAnularId] = useState<string | null>(null);
  const [selectedFacturas, setSelectedFacturas] = useState<Set<string>>(new Set());
  const [isSendingToAEAT, setIsSendingToAEAT] = useState(false);
  const [sendingToFACe, setSendingToFACe] = useState<Set<string>>(new Set());
  const [cambiandoEstado, setCambiandoEstado] = useState<Set<string>>(new Set());

  // Estado para importar desde imagen
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [facturasRes, clientesRes] = await Promise.all([
        fetchWithAuth('/facturas'),
        fetchWithAuth('/clientes'),
      ]);
      if (!facturasRes.ok || !clientesRes.ok) throw new Error('No se pudieron obtener los datos');
      const [facturasData, clientesData] = await Promise.all([facturasRes.json(), clientesRes.json()]);
      setFacturas(facturasData);
      setClientes(clientesData);
    } catch (error) {
      console.error('Error loading facturas:', error);
      toast.error('Error al cargar datos', { description: error instanceof Error ? error.message : 'Error desconocido' });
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const enrichedFacturas = useMemo(() => {
    const clienteMap = new Map(clientes.map(c => [c.id, c.nombre]));
    return facturas.map(f => ({
      ...f,
      clienteNombre: clienteMap.get(f.clienteId) || 'Cliente Desconocido',
    }));
  }, [facturas, clientes]);

  const sortedAndFilteredFacturas = useMemo(() => {
    const filtered = enrichedFacturas.filter(f => 
        f.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.clienteNombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return [...filtered].sort((a, b) => {
        const valA = a[sortConfig.key] ?? '';
        const valB = b[sortConfig.key] ?? '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });
  }, [enrichedFacturas, searchTerm, sortConfig]);

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleAnularConfirm = (motivo: string) => {
    if (!anularId) return;

    // Validar que se haya ingresado un motivo
    if (!motivo.trim()) {
      toast.error('Motivo requerido', {
        description: 'Debes especificar el motivo de la anulación según normativa VeriFactu'
      });
      return;
    }

    const promise = fetchWithAuth(`/facturas/${anularId}/anular`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motivoAnulacion: motivo.trim() })
    }).then(res => {
      if (!res.ok) throw new Error('No se pudo anular la factura');
      return res.json();
    });

    toast.promise(promise, {
      loading: 'Anulando factura...',
      success: () => {
        fetchData();
        setAnularId(null);
        return 'Factura anulada con éxito';
      },
      error: (err) => {
        return `Error: ${err.message}`;
      },
    });
  };

  const handleDownloadXML = async (factura: Factura) => {
    try {
      // Si la factura ya está firmada, descargar el XML firmado
      if (factura.signedXml) {
        const blob = new Blob([factura.signedXml], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura-${factura.numero}-firmada.xml`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('XML Facturae descargado', {
          description: `Firmado el ${new Date(factura.signedAt!).toLocaleDateString('es-ES')}`
        });
      } else {
        toast.info('Factura sin firmar', {
          description: 'Esta factura aún no ha sido firmada con certificado digital. Edita la factura y pulsa "Registrar con Certificado".'
        });
      }
    } catch (error) {
      toast.error('Error al descargar XML', { description: error instanceof Error ? error.message : 'Error desconocido' });
    }
  };

  const handleDownloadPDF = (facturaId: string) => {
    // Navegar a la página de edición y abrir la vista previa
    navigate(`/facturas/${facturaId}/edit?showPreview=true`);
  };

  const handleValidateChain = async () => {
    try {
      const res = await fetchWithAuth('/api/verifactu/validate-chain');
      if (!res.ok) throw new Error('Error al validar la cadena');
      const data = await res.json();

      if (data.valid) {
        toast.success('Cadena de integridad válida', {
          description: 'Todas las facturas están correctamente encadenadas.'
        });
      } else {
        toast.error('Errores en la cadena de integridad', {
          description: `Se encontraron ${data.errors.length} errores. Revisa los logs de auditoría.`
        });
      }
    } catch (error) {
      toast.error('Error al validar cadena', { description: error instanceof Error ? error.message : 'Error desconocido' });
    }
  };

  const handleExportAEAT = async (format: 'json' | 'csv' = 'json') => {
    try {
      const res = await fetchWithAuth(`/facturas/export-aeat?format=${format}`);
      if (!res.ok) throw new Error('Error al exportar facturas');

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verifactu-export-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Exportación AEAT completada', {
        description: `Archivo ${format.toUpperCase()} descargado correctamente`
      });
    } catch (error) {
      toast.error('Error al exportar', { description: error instanceof Error ? error.message : 'Error desconocido' });
    }
  };

  const handleSendToAEAT = async () => {
    if (selectedFacturas.size === 0) {
      toast.error('No hay facturas seleccionadas', {
        description: 'Selecciona al menos una factura para enviar'
      });
      return;
    }

    const facturasArray = Array.from(selectedFacturas);

    // Verificar que todas las facturas tengan Verifactu
    const facturasNoRegistradas = facturasArray.filter(id => {
      const factura = facturas.find(f => f.id === id);
      return !factura?.verifactu || factura.estado === 'Borrador';
    });

    if (facturasNoRegistradas.length > 0) {
      toast.error('Facturas no válidas seleccionadas', {
        description: `${facturasNoRegistradas.length} factura(s) sin registro Verifactu o en borrador`
      });
      return;
    }

    setIsSendingToAEAT(true);

    try {
      const res = await fetchWithAuth('/verifactu/enviar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Sin `entorno`: lo decide el servidor con la config VeriFactu del usuario.
        body: JSON.stringify({
          facturaIds: facturasArray
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Error al enviar a AEAT');
      }

      const data = await res.json();

      if (data.success) {
        // Actualizar estado local inmediatamente (optimistic update)
        const fechaEnvio = new Date().toISOString();
        setFacturas(prevFacturas =>
          prevFacturas.map(f =>
            facturasArray.includes(f.id!)
              ? { ...f, enviadaAEAT: true, fechaEnvioAEAT: fechaEnvio }
              : f
          )
        );

        toast.success('Envío a AEAT completado', {
          description: `${data.estadisticas.exitosas} facturas enviadas correctamente`
        });
        setSelectedFacturas(new Set());

        // Recargar desde servidor para confirmar
        fetchData();
      } else {
        toast.error('Error en el envío', {
          description: data.message || 'Algunas facturas no pudieron enviarse'
        });
      }
    } catch (error) {
      toast.error('Error al enviar a AEAT', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
    } finally {
      setIsSendingToAEAT(false);
    }
  };

  const handleCambiarEstado = async (factura: Factura, nuevoEstado: InvoiceStatus) => {
    if (!factura.id || nuevoEstado === factura.estado) return;
    const facturaId = factura.id;
    setCambiandoEstado(prev => new Set(prev).add(facturaId));

    try {
      const res = await fetchWithAuth(`/api/facturas/${facturaId}/estado`, {
        method: 'PATCH',
        body: JSON.stringify({ estado: nuevoEstado }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Error al cambiar el estado de la factura');
      }

      setFacturas(prev => prev.map(f => (f.id === facturaId ? { ...f, estado: data.estado } : f)));
      toast.success('Estado actualizado', {
        description: `La factura ${factura.numero} figura ahora como ${data.estado}.`,
      });
    } catch (error) {
      toast.error('Error al cambiar el estado', {
        description: error instanceof Error ? error.message : 'Error desconocido',
        duration: 5000,
      });
    } finally {
      setCambiandoEstado(prev => {
        const newSet = new Set(prev);
        newSet.delete(facturaId);
        return newSet;
      });
    }
  };

  const handleEnviarAFACe = async (facturaId: string) => {
    setSendingToFACe(prev => new Set(prev).add(facturaId));

    try {
      const res = await fetchWithAuth(`/api/face/enviar/${facturaId}`, {
        method: 'POST',
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Error al enviar a FACe');
      }

      const data = await res.json();

      if (data.success) {
        toast.success('Factura enviada a FACe', {
          description: `Número de registro: ${data.numeroRegistro}`,
          duration: 5000,
        });

        // Recargar datos para actualizar el estado
        fetchData();
      } else {
        toast.error('Error al enviar a FACe', {
          description: data.errores?.map((e: any) => e.descripcion).join(', ') || data.message,
          duration: 7000,
        });
      }
    } catch (error) {
      toast.error('Error al enviar a FACe', {
        description: error instanceof Error ? error.message : 'Error desconocido',
        duration: 5000,
      });
    } finally {
      setSendingToFACe(prev => {
        const newSet = new Set(prev);
        newSet.delete(facturaId);
        return newSet;
      });
    }
  };

  const toggleFacturaSelection = (facturaId: string | undefined) => {
    if (!facturaId) return;
    setSelectedFacturas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(facturaId)) {
        newSet.delete(facturaId);
      } else {
        newSet.add(facturaId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedFacturas.size === sortedAndFilteredFacturas.length) {
      setSelectedFacturas(new Set());
    } else {
      const allIds = sortedAndFilteredFacturas
        .filter(f => f.verifactu && f.estado !== 'Borrador' && !f.enviadaAEAT)
        .map(f => f.id!)
        .filter(Boolean);
      setSelectedFacturas(new Set(allIds));
    }
  };

  // Importar factura desde imagen
  const handleImportFromImage = async (file: File) => {
    setIsImporting(true);

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('No autenticado');

      // Convertir archivo a Base64
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await fetch('/api/facturas/import-from-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileBase64,
          fileName: file.name,
          mimeType: file.type
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al procesar la imagen');
      }

      const result = await response.json();

      // Cerrar dialog
      setShowImportDialog(false);

      // Navegar a FacturaForm con los datos importados
      navigate('/facturas/nueva', { state: { importedData: result.data } });

      toast.success('Datos extraídos', {
        description: 'Revisa y completa la información antes de guardar.'
      });
    } catch (error) {
      toast.error('Error al importar', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
    } finally {
      setIsImporting(false);
    }
  };

  const SortableHeader: React.FC<{ sortKey: SortKey, label: string }> = ({ sortKey, label }) => (
      <TableHead onClick={() => handleSort(sortKey)} className="cursor-pointer hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">{label} {sortConfig.key === sortKey && <ChevronsUpDown className="h-4 w-4"/>}</div>
      </TableHead>
  );

  return (
    <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
            <div>
                <h1 className="text-3xl font-bold tracking-tighter">Facturas</h1>
                <p className="text-muted-foreground mt-1">Gestiona todas tus facturas emitidas.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <EntornoAEATBadge />
                {selectedFacturas.size > 0 && (
                    <Button
                        onClick={handleSendToAEAT}
                        disabled={isSendingToAEAT}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        {isSendingToAEAT ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <Send className="mr-2 h-4 w-4" />
                                Enviar a AEAT ({selectedFacturas.size})
                            </>
                        )}
                    </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleValidateChain}>
                    <Shield className="mr-2 h-4 w-4" /> Validar Cadena
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExportAEAT('json')}>
                    <Download className="mr-2 h-4 w-4" /> Exportar AEAT
                </Button>
                <DataTableExport data={sortedAndFilteredFacturas} columns={FACTURA_COLUMNS} fileName="facturas"/>
                <Button variant="outline" onClick={() => setShowImportDialog(true)}><Upload className="mr-2 h-4 w-4" /> Importar</Button>
                <Button onClick={() => navigate('/facturas/nueva')}><Plus className="mr-2 h-4 w-4" /> Nueva Factura</Button>
            </div>
        </div>

        <Card>
            <CardHeader>
                <Input
                    placeholder="Buscar por número o cliente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-sm"
                />
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-12">
                                <Checkbox
                                    checked={selectedFacturas.size > 0 && selectedFacturas.size === sortedAndFilteredFacturas.filter(f => f.verifactu && f.estado !== 'Borrador' && !f.enviadaAEAT).length}
                                    onCheckedChange={toggleSelectAll}
                                    aria-label="Seleccionar todas"
                                />
                            </TableHead>
                            <SortableHeader sortKey='numero' label='Nº' />
                            <SortableHeader sortKey='clienteNombre' label='Cliente' />
                            <SortableHeader sortKey='fecha' label='Emisión' />
                            <SortableHeader sortKey='total' label='Total' />
                            <SortableHeader sortKey='estado' label='Estado' />
                            <TableHead>Verifactu</TableHead>
                            <TableHead className="text-right pr-6">Acciones</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow><TableCell colSpan={8} className="h-24 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-primary"/></TableCell></TableRow>
                        ) : sortedAndFilteredFacturas.length === 0 ? (
                            <TableRow><TableCell colSpan={8} className="h-24 text-center text-muted-foreground">No se encontraron facturas.</TableCell></TableRow>
                        ) : (
                            sortedAndFilteredFacturas.map(factura => (
                                <TableRow key={factura.id} onDoubleClick={() => navigate(`/facturas/${factura.id}/edit`)} className="hover:bg-muted/50 cursor-pointer">
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={selectedFacturas.has(factura.id!)}
                                            onCheckedChange={() => toggleFacturaSelection(factura.id)}
                                            disabled={!factura.verifactu || factura.estado === 'Borrador' || factura.enviadaAEAT}
                                            aria-label={`Seleccionar factura ${factura.numero}`}
                                            title={factura.enviadaAEAT ? `Ya enviada a AEAT el ${new Date(factura.fechaEnvioAEAT!).toLocaleDateString('es-ES')}` : ''}
                                        />
                                    </TableCell>
                                    <TableCell className="font-medium">{factura.numero}</TableCell>
                                    <TableCell className="text-muted-foreground">{factura.clienteNombre}</TableCell>
                                    <TableCell className="text-muted-foreground">{new Date(factura.fecha).toLocaleDateString()}</TableCell>
                                    <TableCell className="text-right font-mono">{factura.total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        {ESTADOS_COBRO.includes(factura.estado as InvoiceStatus) ? (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger
                                                    disabled={cambiandoEstado.has(factura.id!)}
                                                    aria-label={`Cambiar estado de cobro de ${factura.numero}`}
                                                    className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                                                >
                                                    <Badge variant={getStatusBadgeVariant(factura.estado as InvoiceStatus)} className="cursor-pointer gap-1">
                                                        {factura.estado}
                                                        <ChevronDown className="h-3 w-3" aria-hidden="true" />
                                                    </Badge>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="start">
                                                    {ESTADOS_COBRO.map(estado => (
                                                        <DropdownMenuItem key={estado} onSelect={() => handleCambiarEstado(factura, estado)}>
                                                            <Check className={cn('mr-2 h-4 w-4', estado !== factura.estado && 'invisible')} aria-hidden="true" />
                                                            {estado}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        ) : (
                                            <Badge variant={getStatusBadgeVariant(factura.estado as InvoiceStatus)}>{factura.estado}</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {factura.verifactu && (
                                                <>
                                                    <span aria-label="Registrada en Verifactu"><ShieldCheck className="h-4 w-4 text-green-600" /></span>
                                                    <span className="text-xs text-muted-foreground" title={`Hash: ${factura.verifactu.hash.substring(0, 8)}...`}>
                                                        #{factura.verifactu.numeroRegistro}
                                                    </span>
                                                </>
                                            )}
                                            {factura.signedAt && (
                                                <span aria-label={`Firmada: ${new Date(factura.signedAt).toLocaleDateString()}`}><CheckCircle2 className="h-4 w-4 text-blue-600" /></span>
                                            )}
                                            {factura.enviadaFACe && (
                                                <span aria-label={`Enviada a FACe: ${factura.fechaEnvioFACe ? new Date(factura.fechaEnvioFACe).toLocaleDateString() : 'Sí'}`}><Building2 className="h-4 w-4 text-purple-600" /></span>
                                            )}
                                            {!factura.verifactu && factura.estado === 'Borrador' && (
                                                <span aria-label="Borrador sin registro"><ShieldAlert className="h-4 w-4 text-muted-foreground" /></span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/facturas/${factura.id}/edit`)}} title="Editar"><Pencil className="w-4 h-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDownloadPDF(factura.id!)}} title="Descargar PDF"><FileText className="w-4 h-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDownloadXML(factura)}} title="Descargar XML Facturae"><FileCode className="w-4 h-4" /></Button>
                                        {(() => {
                                            const cliente = clientes.find(c => c.id === factura.clienteId);
                                            const isAAPP = cliente?.esAdministracionPublica;
                                            const canSendToFACe = isAAPP &&
                                                                  factura.estado !== 'Borrador' &&
                                                                  factura.estado !== 'Anulada' &&
                                                                  !factura.enviadaFACe;
                                            const isSending = sendingToFACe.has(factura.id!);

                                            return canSendToFACe && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEnviarAFACe(factura.id!);
                                                    }}
                                                    title="Enviar a FACe (Administración Pública)"
                                                    disabled={isSending}
                                                >
                                                    {isSending ? (
                                                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                                                    ) : (
                                                        <Building2 className="w-4 h-4 text-blue-600" />
                                                    )}
                                                </Button>
                                            );
                                        })()}
                                        {factura.estado !== 'Borrador' && factura.estado !== 'Anulada' && (
                                            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setAnularId(factura.id ?? null)}} title="Anular Factura"><Ban className="w-4 h-4 text-orange-600" /></Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>

        <AnularFacturaDialog
          open={!!anularId}
          onOpenChange={() => setAnularId(null)}
          onConfirm={handleAnularConfirm}
        />

        <ImportFacturaDialog
          open={showImportDialog}
          onOpenChange={setShowImportDialog}
          onImport={handleImportFromImage}
          isImporting={isImporting}
        />
    </div>
  );
}
